/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { Formik } from 'formik';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, FormControl, FormHelperText, InputLabel, OutlinedInput, Typography } from '@mui/material';

import { DefaultLanguage } from 'shared';

import { SimpleNotifyModal } from '../../components/SimpleNotifyModal/SimpleNotifyModal';
import { Namespaces } from '../../i18n';
import { SuperuserUserApiService } from '../../services/SuperuserUserApiService';
import { useFormStyles } from '../../styles/form';
import { ApiClientError } from '../../types/api/ApiClientError';
import { SystemConfiguration } from '../../types/superuser/SystemConfiguration';
import { UpdateSystemConfigurationRequest } from '../../types/superuser/UpdateSystemConfigurationRequest';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { useValidationTranslation } from '../../utils/useValidationTranslation';
import { getInitialValues } from '../SystemConfigurationPage/SystemConfigurationPage.consts';
import { useSystemConfigurationPageStyles } from '../SystemConfigurationPage/SystemConfigurationPage.styles';
import { SystemConfigurationFormValues } from '../SystemConfigurationPage/SystemConfigurationPage.types';
import { SystemConfigurationPlaceholder } from '../SystemConfigurationPage/SystemConfigurationPlaceholder';
import { imprintConfigValidationSchema } from './ImprintConfigurationPage.consts';

export const ImprintConfigurationPage = memo(() => {
  const { vtr } = useValidationTranslation();
  const { t } = useTranslation(Namespaces.Admin);
  const classes = useSystemConfigurationPageStyles();
  const formClasses = useFormStyles();
  useDocumentTitle(t(`${Namespaces.Common}:page_titles.imprint_configuration`));

  const [errorModalState, setErrorModalState] = useState({
    isOpen: false,
    error: {},
  });
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isConfigurationLoading, setIsConfigurationLoading] = useState<boolean>(false);
  const [configurationData, setConfigurationData] = useState<SystemConfiguration | undefined>(undefined);
  const [isOpenSuccessModal, setIsOpenSuccessModal] = useState<boolean>(false);

  const isLoading = useMemo(() => isConfigurationLoading, [isConfigurationLoading]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsConfigurationLoading(true);
        const config = await SuperuserUserApiService.fetchSystemConfiguration();
        setConfigurationData(config.data.payload);
      } finally {
        setIsConfigurationLoading(false);
      }
    };
    void fetchData();
  }, []);

  const initialValues = useMemo(() => {
    if (configurationData) {
      return getInitialValues({
        ...configurationData,
        smtpHost: configurationData.smtpHost ?? '',
        smtpPort: `${configurationData.smtpPort ?? ''}`,
        smtpSecure: configurationData.smtpSecure ?? false,
        smtpUser: configurationData.smtpUser ?? '',
        smtpPass: configurationData.smtpPass ?? '',
        enquiryEmailLanguage: configurationData?.enquiryEmailLanguage || DefaultLanguage,
        smtpFromEmail: configurationData.smtpFromEmail ?? null,
        minCpuLimit: configurationData.minCpuLimit ?? undefined,
        maxCpuLimit: configurationData.maxCpuLimit ?? undefined,
        minMemoryLimit: configurationData.minMemoryLimit ?? null,
        maxMemoryLimit: configurationData.maxMemoryLimit ?? null,
        minStorageLimit: configurationData.minStorageLimit ?? null,
        maxStorageLimit: configurationData.maxStorageLimit ?? null,
        emailAddress: configurationData.emailAddress ?? null,
        emailToken: configurationData.emailToken ?? null,
      });
    }
    return getInitialValues();
  }, [configurationData]);

  const submit = async (values: SystemConfigurationFormValues) => {
    setIsFetching(true);
    try {
      const data: UpdateSystemConfigurationRequest = {
        smtpHost: values.smtpHost?.trim() || null,
        smtpPort: Number(values.smtpPort) || null,
        smtpSecure: values.useSmtp ? values.smtpSecure : null,
        smtpUser: values.smtpUser?.trim() || null,
        smtpPass: values.smtpPass?.trim() || null,
        enquiryEmails: values.enquiryEmails.trim(),
        enquiryEmailLanguage: values.enquiryEmailLanguage,
        allowServiceCreation: values.allowServiceCreation,
        showInstanceCost: values.showInstanceCost,
        showSupportCost: values.showSupportCost,
        minCpuLimit: Number(values.minCpuLimit) || null,
        maxCpuLimit: Number(values.maxCpuLimit) || null,
        minMemoryLimit: Number(values.minMemoryLimit) || null,
        maxMemoryLimit: Number(values.maxMemoryLimit) || null,
        minStorageLimit: Number(values.minStorageLimit) || null,
        maxStorageLimit: Number(values.maxStorageLimit) || null,
        minReplicasLimit: Number(values.minReplicasLimit),
        maxReplicasLimit: Number(values.maxReplicasLimit),
        cpuPricePerMonth: Number(values.cpuPricePerMonth) || 0,
        memoryPricePerMonth: Number(values.memoryPricePerMonth) || 0,
        storagePricePerMonth: Number(values.storagePricePerMonth) || 0,
        cpuRequestToLimitMultiplier: Number(values.cpuRequestToLimitMultiplier) || 1,
        smtpFromEmail: values.smtpFromEmail,
        emailAddress: values.emailAddress,
        emailToken: values.emailToken,
        currency: values.currency,
        imprint: values.imprint,
      };
      await SuperuserUserApiService.updateSystemConfiguration({ data });
      setIsOpenSuccessModal(true);
    } catch (e) {
      const error = e as ApiClientError;
      if (error.response) {
        const jsonObject = JSON.stringify(error.response.data, null, 2);
        setErrorModalState({
          isOpen: true,
          error: jsonObject,
        });
      }
      console.log(error);
    } finally {
      setIsFetching(false);
    }
  };

  return isLoading ? (
    <SystemConfigurationPlaceholder inputs={1} />
  ) : (
    <Box display="flex" flexDirection="column" width="100%" height="100%" gap="24px">
      <Formik<SystemConfigurationFormValues>
        onSubmit={submit}
        initialValues={initialValues}
        validationSchema={imprintConfigValidationSchema}
      >
        {formikProps => (
          <>
            <Typography variant="h2" className={classes.title}>
              {t('imprint_configuration.title')}
            </Typography>
            <form onSubmit={formikProps.handleSubmit}>
              <FormControl
                className={cx(formClasses.formControl, formClasses.widthMedium)}
                variant="outlined"
                error={!!formikProps.touched['imprint'] && !!formikProps.errors['imprint']}
              >
                <InputLabel htmlFor="imprint">{t('imprint_configuration.form.imprint')}</InputLabel>
                <OutlinedInput
                  name="imprint"
                  label={t('imprint_configuration.form.imprint')}
                  value={formikProps.values.imprint}
                  onChange={formikProps.handleChange}
                  onBlur={formikProps.handleBlur}
                  disabled={isFetching}
                  multiline
                  minRows={2}
                />
                <FormHelperText>
                  {!!formikProps.touched['imprint'] && !!formikProps.errors['imprint']
                    ? vtr(formikProps.errors['imprint'] ?? '', {
                        field: t('imprint_configuration.form.imprint'),
                      })
                    : t('imprint_configuration.form.imprint_tooltip')}
                </FormHelperText>
              </FormControl>
              <Button
                type="submit"
                variant="contained"
                className={classes.submitBtn}
                disabled={isFetching || !formikProps.dirty}
              >
                {t('buttons.submit')}
              </Button>
            </form>
            <SimpleNotifyModal
              open={errorModalState.isOpen}
              message={
                errorModalState.error ? (
                  <pre className={classes.modalError}>{errorModalState.error.toString()}</pre>
                ) : (
                  t(`${Namespaces.Common}:errors.unexpected_error`)
                )
              }
              onOk={() => setErrorModalState({ isOpen: false, error: '' })}
            />
            <SimpleNotifyModal
              open={isOpenSuccessModal}
              message={t('system_configuration.change_saved')}
              onOk={() => setIsOpenSuccessModal(false)}
            />
          </>
        )}
      </Formik>
    </Box>
  );
});
